import React, { useRef, useContext, useEffect, useState } from 'react';
import { Observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import List, { ItemDragging } from 'devextreme-react/list';
import DataSource from 'devextreme/data/data_source';
import FadeIn from 'react-fade-in';
import { GlobalHotKeys } from 'react-hotkeys';
import { toast } from 'react-toastify';

import { renderQuickDrawerLoading } from '../../_shared/QuickDrawer';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';

import BookingTypeUpdateStore from '../../../../stores/BookingTypeUpdateStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';
import * as bh from '../../../../utilities/badgeHelper';
import * as oh from '../../../../utilities/operationHelper';
import * as sys from '../../../../utilities/systemHelper';

import './BookingTypeUsers.scss';

function ManageBookingTypes(props) {
    const isMounted = useRef(true);
    const focusTimer = useRef(null);
    const originalActiveUsers = useRef([]);
    const originalInactiveUsers = useRef([]);
    const originalSortedUsers = useRef([]);
    const bookingTypeUpdate = useContext(BookingTypeUpdateStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [sortEnabled, setSortEnabled] = useState(false);

    useEffect(() => {
        if (bookingTypeUpdate.selectedBookingTypeActiveUsers && bookingTypeUpdate.selectedBookingTypeActiveUsers.length > 0) {
            originalActiveUsers.current = [...toJS(bookingTypeUpdate.selectedBookingTypeActiveUsers)];
        }
        if (bookingTypeUpdate.selectedBookingTypeInactiveUsers && bookingTypeUpdate.selectedBookingTypeInactiveUsers.length > 0) {
            originalInactiveUsers.current = [...toJS(bookingTypeUpdate.selectedBookingTypeInactiveUsers)];
        }

        return (() => {
            isMounted.current = false;
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        })
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (bookingTypeUpdate.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = (event) => {
        event.preventDefault();

        if (bookingTypeUpdate.hasUnsavedChanges) {
            bookingTypeUpdate.updateUsers()
                .then(() => {
                    toast.dark(() => <p>Booking Type Updated.</p>);
                    if (fn.isFunction(props.onSuccess)) {
                        props.onSuccess();
                    }
                })
        }
        else if (fn.isFunction(props.onSuccess)) {
            props.onSuccess();
        }
    }

    const handleSortEnable = (event) => {
        originalSortedUsers.current = ([...toJS(bookingTypeUpdate.selectedBookingTypeActiveUsers)]);
        setSortEnabled(true);
    }

    const handleSortConfirm = (event) => {
        if (bookingTypeUpdate.hasUnsavedChanges) {
            bookingTypeUpdate.updateUsers()
                .then(() => {
                    setSortEnabled(false);
                    toast.dark(() => <p>Booking Type Updated.</p>);
                })
        }
        else {
            setSortEnabled(false);
        }
    }

    const handleSortCancel = (event) => {
        const ti = bookingTypeUpdate.data.findIndex(d => d.id === bookingTypeUpdate.selectedBookingTypeId);

        if (ti >= 0) {
            bookingTypeUpdate.data[ti].users = originalSortedUsers.current;
            originalSortedUsers.current = null;
        }

        setSortEnabled(false);
    }

    const handleBookingTypeUserReorder = (item) => {
        const ti = bookingTypeUpdate.data.findIndex(d => d.id === bookingTypeUpdate.selectedBookingTypeId);
        const { toIndex, fromIndex } = item;
        let selectedBookingTypeActiveUsers = [...toJS(bookingTypeUpdate.selectedBookingTypeActiveUsers)];

        selectedBookingTypeActiveUsers.splice(fromIndex, 1);
        selectedBookingTypeActiveUsers.splice(toIndex, 0, toJS(bookingTypeUpdate.selectedBookingTypeActiveUsers[fromIndex]));

        for (let i = 0; i < selectedBookingTypeActiveUsers.length; i++) {
            selectedBookingTypeActiveUsers[i].displayOrder = (i + 1);
        }

        for (let i = 0; i < selectedBookingTypeActiveUsers.length; i++) {
            const ii = bookingTypeUpdate.selectedBookingTypeActiveUsers.findIndex(d => d.id === selectedBookingTypeActiveUsers[i].id);
            if (ii >= 0) {
                bookingTypeUpdate.data[ti].users[ii].displayOrder = selectedBookingTypeActiveUsers[i].displayOrder;
            }
        }

        bookingTypeUpdate.data[ti].users = bookingTypeUpdate.data[ti].users.slice().sort((a, b) => a.displayOrder - b.displayOrder);
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleCutoffHoursChange = (event, user) => {
        const ti = bookingTypeUpdate.data.findIndex(d => d.id === bookingTypeUpdate.selectedBookingTypeId);

        if (ti >= 0) {
            const { value } = event.target;
            const ui = bookingTypeUpdate.selectedBookingType.users.findIndex(u => u.id === user.id);
            bookingTypeUpdate.data[ti].users[ui].cutoffHours = parseInt(value, 10);
            bookingTypeUpdate.hasUnsavedChanges = true;
        }
    }

    const handleIsBookingTypeUserChange = (event, user) => {
        const IsBookingTypeUser = isBookingTypeUser(user);
        const ti = bookingTypeUpdate.data.findIndex(d => d.id === bookingTypeUpdate.selectedBookingTypeId);

        if (ti >= 0) {
            if (IsBookingTypeUser) {
                const ui = bookingTypeUpdate.selectedBookingType.users.findIndex(u => u.id === user.id);
                bookingTypeUpdate.data[ti].users.splice(ui, 1);
                bookingTypeUpdate.hasUnsavedChanges = true;
            }
            else {
                bookingTypeUpdate.data[ti].users.push({
                    ...{
                        cutoffHours: sys.configuration.onlineBooking.cutoffHours,
                        displayOrder: 999
                    },
                    ...user
                });
                bookingTypeUpdate.hasUnsavedChanges = true;
            }
        }
    }

    const renderBookingType = (item) => {
        return <div className='form-group mb-0 py-1'>
            <div className='booking-type'>
                <div className='mb-1'>{bh.renderBookingType(item)}</div>
                <h4 className='booking-type-name d-block mb-1 text-truncate'><strong>{item.name}</strong></h4>
                <span className='booking-type-description d-block fs-sm text-truncate' title={item.description}>{item.description}</span>
            </div>
        </div>
    }

    const renderBookingUser = (item, disabled = false) => {
        return <Observer>{() =>
            <div className={'form-group py-1' + (bookingTypeUpdate.selectedBookingType.isRequestOnly ? ' my-1' : '')}>
                <div className='booking-user d-flex w-100 align-items-center'>
                    <div className='flex-1'>
                        <label
                            htmlFor={!sortEnabled ? `booking-user-${item.id}` : ''}
                            className={'booking-user-name d-block text-truncate' + (!sortEnabled ? ' text-info-hover' : '') + (disabled ? ' text-gray-600' : '')}
                        >
                            <strong>{item.fullName}</strong>
                        </label>
                        {
                            !bookingTypeUpdate.selectedBookingType.isRequestOnly ?
                                <div className='d-flex align-items-center'>
                                    <div className='form-group'>
                                        <label className='fs-sm text-gray-600'><strong>Minimum time before the appointment</strong></label>
                                        <div
                                            className='input-group input-group-sm'
                                            style={{ width: '100px' }}
                                        >
                                            <input
                                                type='number'
                                                min={1}
                                                max={96}
                                                className='form-control px-h text-right'
                                                disabled={disabled || sortEnabled}
                                                value={!disabled ? (item.cutoffHours ? item.cutoffHours : sys.configuration.onlineBooking.cutoffHours) : ''}
                                                onChange={(e) => { handleCutoffHoursChange(e, item) }}
                                            />
                                            <div className='input-group-append'>
                                                <span className='input-group-text'>hrs.</span>
                                            </div>
                                        </div>
                                    </div>
                                </div> : null
                        }
                    </div>
                    <div className={'text-right' + (sortEnabled ? ' d-none' : '')}>
                        <div className='custom-control custom-switch'>
                            <input
                                id={`booking-user-${item.id}`}
                                type='checkbox'
                                name={`booking-user-${item.id}`}
                                className='custom-control-input'
                                checked={isBookingTypeUser(item)}
                                onChange={(e) => { handleIsBookingTypeUserChange(e, item) }}
                            />
                            <label
                                htmlFor={`booking-user-${item.id}`}
                                className='custom-control-label'
                            >
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        }</Observer>
    }

    const isBookingTypeUser = (user) => {
        return bookingTypeUpdate.selectedBookingTypeActiveUsers.some(u => user && u.id === user.id);
    }

    return <>
        <Observer>{() =>
            <>
                {
                    (props.drawer === quickDrawer.drawerOpened) ?
                        <GlobalHotKeys
                            keyMap={{
                                close: ['esc'],
                            }}
                            handlers={{
                                close: event => {
                                    handleCancel(event)
                                },
                            }}
                            allowChanges={true}
                        /> : null
                }
            </>
        }</Observer>
        <form onSubmit={handleSubmit}>
            <fieldset disabled={bookingTypeUpdate.isSaving}>
                <div className='quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('appointment', 'update')}
                        action='Manage'
                        category='Online Booking Types'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        <div className='booking-type-users-page body-content'>
                            <Observer>{() =>
                                <>
                                    {
                                        bookingTypeUpdate.isReady && bookingTypeUpdate.selectedBookingType ?
                                            <FadeIn>
                                                <section className='mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            {renderBookingType(bookingTypeUpdate.selectedBookingType)}
                                                        </div>
                                                    </div>
                                                </section>
                                                <section>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <hr />
                                                        </div>
                                                    </div>
                                                </section>
                                                <section>
                                                    <div className='row datasets'>
                                                        <div className='col-12'>
                                                            <div className='d-flex w-100 align-items-center'>
                                                                <strong className='text-gray-600 text-uppercase flex-1 m-0 mr-2'>Available Users</strong>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-link p-0' + (sortEnabled ? ' d-none' : '') + (bookingTypeUpdate.hasUnsavedChanges ? ' cursor-not-allowed' : '')}
                                                                    disabled={bookingTypeUpdate.hasUnsavedChanges}
                                                                    onClick={handleSortEnable}
                                                                >
                                                                    Change display order
                                                                </button>
                                                            </div>
                                                            <div>
                                                                {
                                                                    bookingTypeUpdate.selectedBookingTypeActiveUsers && bookingTypeUpdate.selectedBookingTypeActiveUsers.length > 0 ?
                                                                        <List
                                                                            dataSource={
                                                                                new DataSource({
                                                                                    key: "id",
                                                                                    store: bookingTypeUpdate.selectedBookingTypeActiveUsers,
                                                                                    paginate: false,
                                                                                })
                                                                            }
                                                                            activeStateEnabled={false}
                                                                            focusStateEnabled={false}
                                                                            hoverStateEnabled={false}
                                                                            itemRender={(data) => { return renderBookingUser(data) }}
                                                                        >
                                                                            {
                                                                                sortEnabled ?
                                                                                    <ItemDragging
                                                                                        allowReordering={true}
                                                                                        onReorder={handleBookingTypeUserReorder}
                                                                                    >
                                                                                    </ItemDragging> : null
                                                                            }
                                                                        </List> : null
                                                                }
                                                                {
                                                                    !sortEnabled && bookingTypeUpdate.selectedBookingTypeInactiveUsers && bookingTypeUpdate.selectedBookingTypeInactiveUsers.length > 0 ?
                                                                        <List
                                                                            dataSource={
                                                                                new DataSource({
                                                                                    key: "id",
                                                                                    store: bookingTypeUpdate.selectedBookingTypeInactiveUsers,
                                                                                    paginate: false,
                                                                                })
                                                                            }
                                                                            activeStateEnabled={false}
                                                                            focusStateEnabled={false}
                                                                            hoverStateEnabled={false}
                                                                            itemRender={(data) => { return renderBookingUser(data, true) }}
                                                                        >
                                                                        </List> : null
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                            </FadeIn> : renderQuickDrawerLoading()
                                    }
                                </>
                            }</Observer>
                        </div>
                    </div>
                    <div className='quick-drawer-action'>
                        <div className='row'>
                            <div className='col-12'>
                                {
                                    sortEnabled ?
                                        <div className='float-right'>
                                            <button
                                                type='button'
                                                className='btn btn-link btn-cancel mr-2'
                                                onClick={handleSortCancel}
                                            >Cancel</button>
                                            <button
                                                type='button'
                                                className='btn btn-primary'
                                                onClick={handleSortConfirm}
                                            >Update</button>
                                        </div> :
                                        <div className='float-right'>
                                            <button
                                                type='button'
                                                className='btn btn-link btn-cancel mr-2'
                                                onClick={handleCancel}
                                            >Cancel</button>
                                            <button
                                                type='submit'
                                                className='btn btn-primary'
                                            >Update</button>
                                        </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form >
    </>
}

export default ManageBookingTypes;